import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing, ensureSectionExpanded } from './tasks-help.shared';

/**
 * ?-Hilfe fuer den Routinen-Editor (Anlegen/Bearbeiten einer Vorlage).
 *
 * Der Dialog beantwortet vier Fragen — was ist zu tun, wie oft, wann am Tag,
 * was ist zu belegen. Vier fachliche Aussagen tragen die Tour, alle am echten
 * Template `pages/routines/routine-editor/routine-editor.component.html`
 * geprueft:
 *
 *  1. **Eine Routine ist eine Vorlage, keine Aufgabe.** Die echten Aufgaben
 *     entstehen im naechtlichen Lauf und erscheinen erst zu ihrer
 *     "Sichtbar ab"-Zeit. Beleg: `recurrence.util.ts` (`nextAppearanceLabel`,
 *     "…weil sie erst um 06:00 sichtbar wird (und der Generator sie erst nachts
 *     erzeugt)") und `routine-management-list.component.html` (`nextAppearance()`).
 *  2. **Bearbeiten erzeugt eine neue Version.** Der Editor blendet im
 *     Bearbeiten-Modus das Banner `TASK_ROUTINE_VERSIONING_HINT` ein
 *     ("Speichern erzeugt eine neue Version; laufende Aufgaben bleiben
 *     unveraendert"), Template-Zeilen 18-22 mit dem Kommentar "PUT erzeugt neue
 *     Template-Version, UC-27".
 *  3. **Der Feldtyp "Foto" fehlt hier.** Der eingebettete Checklisten-Editor
 *     laeuft mit `[allowPhoto]="false"` (Template-Zeile 431); der Kommentar
 *     darueber nennt den Grund (eine Routine erzeugt planbar Tag fuer Tag
 *     Bilder). Sonst ist es derselbe Editor wie im Aufgaben-Dialog — deshalb
 *     verweist der Checklisten-Schritt per Aktion auf die Checklisten-Tour.
 *  4. **Pausieren und Archivieren sind zwei verschiedene Enden.** Pausieren
 *     laeuft ueber `pauseToggle()` (`task-api.service.ts:458`:
 *     "`{Paused:true}` = pausieren, `{Paused:false}` = reaktivieren"), die
 *     Routine steht danach auf `TASK_ROUTINE_NEXT_RUN_PAUSED`
 *     ("Pausiert — kein Termin"), bleibt aber erhalten. Archivieren nimmt sie
 *     dauerhaft aus den aktiven Listen
 *     (`TASK_ROUTINE_ARCHIVE_CONFIRM_MESSAGE`).
 *     ⚠️ NICHT belegt ist, was mit **bereits erzeugten** Aufgaben beim
 *     Pausieren passiert — im Frontend steht dazu nichts. Der Schritt sagt
 *     deshalb nur, dass kuenftige Laeufe stoppen, und behauptet nichts ueber
 *     den Bestand.
 *
 * Alle Schritte laufen mit `popoverOutsideDialog`: der Dialog ist `size="lg"`,
 * ein Popover INNERHALB verdeckt genau das Feld, das es erklaert.
 * `panelIndex: -1` ueberall — der Dialog ist kein Material-Accordion; ohne den
 * Wert liefe der Coordinator in den Legacy-Zweig und klickte jeden Kopf an.
 *
 * Die einklappbaren Abschnitte (Zeitfenster, Checkliste) brauchen
 * {@link ensureSectionExpanded}: `xds-section` versteckt den Body per
 * `[hidden]`, der Anker bleibt also im DOM und `skipIf` wuerde NICHT greifen.
 */
const dialogStep = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

export const TASK_ROUTINE_EDITOR_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskRoutineEditorHelp,
  version: 1,
  title: 'TASK_ROUTINE_EDITOR_HELP_TOUR_TITLE',
  description: 'TASK_ROUTINE_EDITOR_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Start aus dem Hilfe-Hub: zur Routinen-Seite navigieren, dann "Routine
  // anlegen" klicken. Aus dem bereits offenen Dialog heraus startet der
  // ?-Knopf im Kopf direkt.
  launchContext: {
    route: '/tasks/routines',
    opens: { openerAnchor: AnchorId.TasksRoutineCreateButton, settleMs: 400 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 155,
  category: 'feature-discovery',
  // Je nach Wiederholungs-Modus fallen zwei bis drei Schritte weg. Ohne
  // Umnummerierung zaehlte das Popover "3 / 10 → 6 / 10" und der Nutzer sucht
  // die fehlenden.
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_ROUTINE_EDITOR_HELP_TOUR_TITLE',
      steps: [
        {
          id: 're-welcome',
          anchor: AnchorId.RoutineEditorDialog,
          spotlightAnchor: AnchorId.RoutineEditorDialog,
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.WELCOME.CONTENT',
          audioStepId: 're-welcome-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: {
            ...dialogStep,
            waitForOpen: true,
            outsideDialog: true,
            highlightDialogHeader: true,
          },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Ziel und Prioritaet gelten fuer JEDE erzeugte Aufgabe. Anker sitzt
          // am Zielfeld, der Spotlight auf dem ganzen Grunddaten-Block —
          // sonst wirkt der Satz "gilt fuer jede Aufgabe" wie eine Aussage
          // ueber ein einzelnes Eingabefeld.
          id: 're-basics',
          anchor: AnchorId.RoutineEditorTarget,
          spotlightAnchor: AnchorId.RoutineEditorBasics,
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.BASICS.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.BASICS.CONTENT',
          audioStepId: 're-basics-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // Die Weiche: fester Termin (Kalender) gegen "Tage nach Erledigung"
          // (Kette). Von dieser Wahl haengen die beiden naechsten Schritte ab.
          id: 're-repeat-mode',
          anchor: AnchorId.RoutineEditorRepeat,
          spotlightAnchor: AnchorId.RoutineEditorRepeat,
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.REPEAT_MODE.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.REPEAT_MODE.CONTENT',
          audioStepId: 're-repeat-mode-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // ⚠️ BEWUSST KEIN `onBeforeShow`, das auf "fester Termin"
          // umschaltet: das schriebe in die Daten des Nutzers. Muster und
          // Intervall stehen nur im festen Zeitplan im DOM
          // (`*ngIf="isFixedSchedule"`), wer "Tage nach Erledigung" gewaehlt
          // hat, ueberspringt den Schritt einfach.
          id: 're-pattern',
          anchor: AnchorId.RoutineEditorPattern,
          spotlightAnchor: AnchorId.RoutineEditorPattern,
          additionalSpotlightAnchors: [AnchorId.RoutineEditorInterval],
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.PATTERN.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.PATTERN.CONTENT',
          audioStepId: 're-pattern-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.RoutineEditorPattern),
        },
        {
          // Ebenfalls ohne Umschalten (siehe `re-pattern`). Die Chips haengen
          // zusaetzlich am Muster (`*ngIf="showWeekdays"`), der Monatsmodus an
          // Monatlich/Jaehrlich — beides ist entweder-oder, nie beides.
          id: 're-weekdays',
          anchor: AnchorId.RoutineEditorWeekdays,
          spotlightAnchor: AnchorId.RoutineEditorWeekdays,
          additionalSpotlightAnchors: [AnchorId.RoutineEditorMonthMode],
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.WEEKDAYS.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.WEEKDAYS.CONTENT',
          audioStepId: 're-weekdays-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.RoutineEditorWeekdays),
        },
        {
          id: 're-validity',
          anchor: AnchorId.RoutineEditorExceptions,
          spotlightAnchor: AnchorId.RoutineEditorExceptions,
          additionalSpotlightAnchors: [AnchorId.RoutineEditorValidity],
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.VALIDITY.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.VALIDITY.CONTENT',
          audioStepId: 're-validity-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          // 09.10.2026: Ausnahmen und Laufzeit stehen nur beim festen Termin
          // (`*ngIf="isFixedSchedule"`) — im Modus „X Tage nach Erledigung“ hing die Station.
          optional: true,
          skipIf: () => anchorMissing(AnchorId.RoutineEditorExceptions),
        },
        {
          // Drei Zeiten, drei Bedeutungen — der haeufigste Irrtum ist,
          // "Sichtbar ab" fuer die Faelligkeit zu halten.
          id: 're-timeslots',
          anchor: AnchorId.RoutineEditorTimeslots,
          spotlightAnchor: AnchorId.RoutineEditorTimeslots,
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.TIMESLOTS.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.TIMESLOTS.CONTENT',
          audioStepId: 're-timeslots-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          onBeforeShow: () =>
            ensureSectionExpanded(AnchorId.RoutineEditorTimeslots),
        },
        {
          // Der Erweitert-Block des ersten Fensters ist zugeklappt, solange
          // niemand ihn braucht — dann fehlt der Anker und der Schritt faellt
          // weg.
          id: 're-slot-advanced',
          anchor: AnchorId.RoutineEditorSlotAdvanced,
          spotlightAnchor: AnchorId.RoutineEditorSlotAdvanced,
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.SLOT_ADVANCED.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.SLOT_ADVANCED.CONTENT',
          audioStepId: 're-slot-advanced-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.RoutineEditorSlotAdvanced),
        },
        {
          id: 're-checklist',
          anchor: AnchorId.RoutineEditorChecklist,
          spotlightAnchor: AnchorId.RoutineEditorChecklist,
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.CHECKLIST.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.CHECKLIST.CONTENT',
          audioStepId: 're-checklist-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          onBeforeShow: () =>
            ensureSectionExpanded(AnchorId.RoutineEditorChecklist),
          actions: [
            {
              labelKey: 'TUTORIAL.TASK_ROUTINE_EDITOR.CHECKLIST.ACTION',
              icon: 'fa-light fa-list-check',
              startTour: TourFeature.TaskChecklistHelp,
              variant: 'secondary',
            },
          ],
        },
        {
          // Pausieren/Fortsetzen tragen denselben Anker, Archivieren steht nur
          // im Bearbeiten-Modus mit passender `AllowedAction` — beim Anlegen
          // bleibt nur "Speichern" im Spotlight.
          id: 're-footer',
          anchor: AnchorId.RoutineEditorSave,
          spotlightAnchor: AnchorId.RoutineEditorSave,
          additionalSpotlightAnchors: [
            AnchorId.RoutineEditorPause,
            AnchorId.RoutineEditorArchive,
          ],
          title: 'TUTORIAL.TASK_ROUTINE_EDITOR.FOOTER.TITLE',
          content: 'TUTORIAL.TASK_ROUTINE_EDITOR.FOOTER.CONTENT',
          audioStepId: 're-footer-v2',
          placement: 'top',
          align: 'end',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
